/* Mot de passe oublié + réinitialisation — Factumis app.
   Deux vues (une par route), câblées séparément par le routeur (Task 7) :

   - OubliMotDePasse (/oubli-mot-de-passe) : saisie de l'email →
     authClient.requestPasswordReset({ email, redirectTo: '/reinitialiser' }).
     On affiche TOUJOURS un message neutre en cas de succès pour ne pas
     révéler l'existence d'un compte (le backend renvoie déjà un statut
     neutre quand l'email est inconnu).

   - Reinitialiser (/reinitialiser?token=…) : le mail de Better Auth pointe
     vers /api/auth/reset-password/:token qui redirige ensuite vers ce
     `redirectTo` en y ajoutant `?token=VALID_TOKEN` (ou `?error=…`). On lit
     donc le token dans la query, on saisit le nouveau mot de passe (min 8)
     puis authClient.resetPassword({ newPassword, token }) → /connexion. */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/auth-client';
import { AuthLayout } from './AuthLayout';
import { Button, Field, Input } from '../components/ui';
import { Icon } from '../lib/icons';

/* Mappe les erreurs Better Auth vers un message FR utilisateur. */
function frenchMessage(error: { code?: string; status?: number }): string {
  if (error.code === 'INVALID_TOKEN' || error.code === 'TOKEN_EXPIRED')
    return 'Lien invalide ou expiré.';
  if (error.code === 'PASSWORD_TOO_SHORT')
    return 'Le mot de passe doit contenir au moins 8 caractères.';
  if (error.status === 429) return 'Trop de tentatives, réessayez dans un instant.';
  return 'Une erreur est survenue, réessayez.';
}

/* Étape 1 — /oubli-mot-de-passe : demande d'un lien de réinitialisation. */
export function OubliMotDePasse() {
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const busy = loading;

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setErr(null);
    setLoading(true);
    const { error } = await authClient.requestPasswordReset({
      email,
      redirectTo: '/reinitialiser',
    });
    setLoading(false);
    // Le backend renvoie un statut neutre même si le compte n'existe pas :
    // on n'affiche une erreur que pour un vrai problème réseau/serveur, et
    // jamais un "compte introuvable" (anti-énumération d'emails).
    if (error && error.status !== 404) {
      setErr(frenchMessage(error));
      return;
    }
    setSent(true);
  }

  return (
    <AuthLayout
      title="Mot de passe oublié"
      subtitle="Saisissez votre email pour recevoir un lien de réinitialisation."
    >
      {err ? (
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          {err}
        </div>
      ) : null}

      {sent ? (
        <p className="auth-sub">Si un compte existe, un lien a été envoyé.</p>
      ) : (
        <>
          <form className="auth-form" onSubmit={onSubmit} noValidate>
            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="vous@entreprise.fr"
                disabled={busy}
                required
              />
            </Field>
            <div className="auth-actions">
              <Button type="submit" full loading={loading} disabled={busy}>
                Envoyer le lien
              </Button>
            </div>
          </form>

          <div className="auth-foot-row">
            <Link to="/connexion" className="auth-link">
              Retour à la connexion
            </Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
}

/* Étape 2 — /reinitialiser?token=… : définition d'un nouveau mot de passe. */
export function Reinitialiser() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const urlError = searchParams.get('error');

  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const busy = loading;

  // Token absent (accès direct) ou rejeté par le callback Better Auth
  // (redirection avec ?error=INVALID_TOKEN) → lien inexploitable.
  if (!token || urlError) {
    return (
      <AuthLayout title="Réinitialiser le mot de passe" subtitle="Choisissez un nouveau mot de passe.">
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          Lien invalide ou expiré.
        </div>
        <div className="auth-foot-row">
          <Link to="/oubli-mot-de-passe" className="auth-link">
            Demander un nouveau lien
          </Link>
        </div>
      </AuthLayout>
    );
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setErr(null);
    if (password.length < 8) {
      setErr('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    setLoading(true);
    const { error } = await authClient.resetPassword({ newPassword: password, token: token! });
    setLoading(false);
    if (error) {
      setErr(frenchMessage(error));
      return;
    }
    navigate('/connexion');
  }

  return (
    <AuthLayout title="Réinitialiser le mot de passe" subtitle="Choisissez un nouveau mot de passe.">
      {err ? (
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          {err}
        </div>
      ) : null}

      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <Field label="Nouveau mot de passe" help="8 caractères minimum.">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            disabled={busy}
            required
          />
        </Field>
        <div className="auth-actions">
          <Button type="submit" full loading={loading} disabled={busy}>
            Réinitialiser
          </Button>
        </div>
      </form>

      <div className="auth-foot-row">
        <Link to="/connexion" className="auth-link">
          Retour à la connexion
        </Link>
      </div>
    </AuthLayout>
  );
}
